import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft, Save, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export function CadastrarInvestimentoPage() {
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState({
    nome: '',
    tipo: '',
    valorInvestido: '',
    dataInvestimento: new Date().toISOString().split('T')[0],
    dataVencimento: '',
    rentabilidade: '',
    instituicao: '',
    observacoes: ''
  })

  const [isLoading, setIsLoading] = useState(false)

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.nome || !formData.tipo || !formData.valorInvestido || !formData.dataInvestimento) {
      alert('Por favor, preencha todos os campos obrigatórios.')
      return
    }

    if (formData.dataVencimento && new Date(formData.dataVencimento) <= new Date(formData.dataInvestimento)) {
      alert('A data de vencimento deve ser posterior à data do investimento.')
      return
    }

    setIsLoading(true)

    try {
      const novoInvestimento = {
        nome: formData.nome,
        tipo: formData.tipo,
        valorInvestido: parseFloat(formData.valorInvestido),
        valorAtual: parseFloat(formData.valorInvestido), // Inicialmente igual ao valor investido
        dataInvestimento: formData.dataInvestimento,
        dataVencimento: formData.dataVencimento || undefined,
        rentabilidade: formData.rentabilidade ? parseFloat(formData.rentabilidade) : undefined,
        instituicao: formData.instituicao,
        observacoes: formData.observacoes || undefined,
        status: 'ativo' as const
      }

      // Aqui você salvaria no store
      console.log('Novo investimento:', novoInvestimento)
      
      // Redirecionar para a listagem
      navigate('/financas/administracao/investimentos')
    } catch (error) {
      console.error('Erro ao cadastrar investimento:', error)
      alert('Erro ao cadastrar investimento. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    navigate('/financas/administracao/investimentos')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Cadastrar Novo Investimento</h1>
          <p className="text-muted-foreground">
            Registre um novo investimento da igreja
          </p>
        </div>
      </div>

      {/* Formulário */}
      <Card>
        <CardHeader>
          <CardTitle>Informações do Investimento</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Investimento *</Label>
                <Input
                  id="nome"
                  placeholder="Ex: CDB Banco do Brasil"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo de Investimento *</Label>
                <Select 
                  value={formData.tipo} 
                  onValueChange={(value) => handleInputChange('tipo', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CDB">CDB</SelectItem>
                    <SelectItem value="Tesouro Direto">Tesouro Direto</SelectItem>
                    <SelectItem value="LCI">LCI</SelectItem>
                    <SelectItem value="LCA">LCA</SelectItem>
                    <SelectItem value="Poupança">Poupança</SelectItem>
                    <SelectItem value="Fundo de Investimento">Fundo de Investimento</SelectItem>
                    <SelectItem value="Outros">Outros</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="valorInvestido">Valor Investido *</Label>
                <Input
                  id="valorInvestido"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={formData.valorInvestido}
                  onChange={(e) => handleInputChange('valorInvestido', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="rentabilidade">Rentabilidade (% a.a.)</Label>
                <Input
                  id="rentabilidade"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Ex: 5.5"
                  value={formData.rentabilidade}
                  onChange={(e) => handleInputChange('rentabilidade', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dataInvestimento">Data do Investimento *</Label>
                <Input
                  id="dataInvestimento"
                  type="date"
                  value={formData.dataInvestimento}
                  onChange={(e) => handleInputChange('dataInvestimento', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dataVencimento">Data de Vencimento</Label>
                <Input
                  id="dataVencimento"
                  type="date"
                  value={formData.dataVencimento}
                  onChange={(e) => handleInputChange('dataVencimento', e.target.value)}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="instituicao">Instituição Financeira</Label>
                <Input
                  id="instituicao"
                  placeholder="Ex: Banco do Brasil, Caixa Econômica Federal"
                  value={formData.instituicao}
                  onChange={(e) => handleInputChange('instituicao', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                placeholder="Observações adicionais sobre o investimento (opcional)"
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                rows={3}
              />
            </div>

            {/* Botões */}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isLoading ? 'Salvando...' : 'Salvar Investimento'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}